import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Input,
  Row,
  Space,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import usePaymentTransactions from "hooks/finance/usePaymentTransactions";
import PaymentTransactionsTable from "./PaymentTransactionsTable";
import IssueRefundDrawer from "./IssueRefundDrawer";

const { RangePicker } = DatePicker;

const buildDefaultDateRange = () => ({
  start: dayjs().startOf("month").toISOString(),
  end: dayjs().endOf("day").toISOString(),
});

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  const currencyCode = (currency || "USD").toUpperCase();

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch (err) {
    return `${numericAmount.toFixed(2)} ${currencyCode}`;
  }
};

function MetricTile({ label, value, icon }) {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        background: "#fff",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          border: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6b7280",
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 11, display: "block" }}>
          {label}
        </Typography.Text>
        <div
          style={{
            marginTop: 2,
            fontSize: 16,
            lineHeight: 1.2,
            fontWeight: 600,
            color: "#111827",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function TotalsStrip({ totals }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Row gutter={[8, 8]}>
        <Col xs={24} md={10}>
          <MetricTile
            label="Total Amount"
            value={formatCurrency(totals.totalAmount, totals.primaryCurrency)}
            icon={<DollarOutlined />}
          />
        </Col>
        <Col xs={12} md={7}>
          <MetricTile
            label="Succeeded"
            value={totals.succeededCount}
            icon={<CheckCircleOutlined />}
          />
        </Col>
        <Col xs={12} md={7}>
          <MetricTile
            label="Failed"
            value={totals.failedCount}
            icon={<CloseCircleOutlined />}
          />
        </Col>
      </Row>
    </div>
  );
}

export default function PaymentTransactionsPage() {
  const {
    paymentTransactions,
    paymentTransactionsMeta,
    fetchPaymentTransactions,
    loading,
  } = usePaymentTransactions();

  const [dateRange, setDateRange] = useState(buildDefaultDateRange);
  const [pagination, setPagination] = useState({ page: 0, size: 20 });
  const [chargeCodeInput, setChargeCodeInput] = useState("");
  const [chargeCodeFilter, setChargeCodeFilter] = useState("");
  const [refundDrawerOpen, setRefundDrawerOpen] = useState(false);
  const [activeTransaction, setActiveTransaction] = useState(null);

  useEffect(() => {
    fetchPaymentTransactions({
      start: dateRange.start,
      end: dateRange.end,
      page: pagination.page,
      size: pagination.size,
      chargeItemCode: chargeCodeFilter || undefined,
    });
  }, [
    dateRange.start,
    dateRange.end,
    pagination.page,
    pagination.size,
    chargeCodeFilter,
    fetchPaymentTransactions,
  ]);

  const totals = useMemo(() => {
    const initialState = {
      totalAmount: 0,
      succeededCount: 0,
      failedCount: 0,
      primaryCurrency: "USD",
    };

    const currencies = new Set();

    paymentTransactions.forEach((tx) => {
      const amount = Number(tx?.amount);
      if (Number.isFinite(amount)) {
        initialState.totalAmount += amount;
      }

      if (tx?.currency) currencies.add(tx.currency.toUpperCase());

      const status = String(tx?.status || "").toLowerCase();
      if (["succeeded", "completed", "success", "paid"].includes(status)) {
        initialState.succeededCount += 1;
      } else if (["failed", "cancelled", "canceled"].includes(status)) {
        initialState.failedCount += 1;
      }
    });

    if (currencies.size === 1) {
      initialState.primaryCurrency = [...currencies][0];
    }

    return initialState;
  }, [paymentTransactions]);

  const handleDateRangeChange = (dates) => {
    if (!dates || dates.length < 2) return;

    setDateRange({
      start: dates[0].startOf("day").toISOString(),
      end: dates[1].endOf("day").toISOString(),
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const setThisMonthRange = () => {
    setDateRange({
      start: dayjs().startOf("month").toISOString(),
      end: dayjs().endOf("day").toISOString(),
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setDateRange(buildDefaultDateRange());
    setPagination({ page: 0, size: 20 });
    setChargeCodeInput("");
    setChargeCodeFilter("");
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="file-invoice-dollar"
          title="Payment Transactions"
          subtitle="View and analyze payment charge transactions by date and charge code"
          className="mb-2 mt-2 flex-1"
        />
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "10px 12px",
          marginBottom: 10,
          background: "#fff",
        }}
      >
        <Row gutter={[10, 10]} style={{ alignItems: "end" }}>
          <Col xs={24} md={12} lg={9}>
            <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
             Choose Date Range
            </Typography.Text>
            <RangePicker
              value={[dayjs(dateRange.start), dayjs(dateRange.end)]}
              onChange={handleDateRangeChange}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={12} lg={7}>
            <Input.Search
              allowClear
              placeholder="Filter by charge code"
              value={chargeCodeInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setChargeCodeInput(nextValue);
                if (!nextValue) {
                  setChargeCodeFilter("");
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }
              }}
              onSearch={(value) => {
                setChargeCodeFilter(value.trim());
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Col>
          <Col xs={24} lg={8} style={{ textAlign: "right" }}>
            <Space wrap style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button onClick={setThisMonthRange}>This Month</Button>
              <Button onClick={clearFilters}>Reset</Button>
            </Space>
          </Col>
        </Row>
      </div>

      <TotalsStrip totals={totals} />

      <PaymentTransactionsTable
        transactions={paymentTransactions}
        loading={loading}
        meta={paymentTransactionsMeta}
        onFetchTransactions={({ page, size }) => setPagination({ page, size })}
        onOpenRefunds={(tx) => {
          setActiveTransaction(tx);
          setRefundDrawerOpen(true);
        }}
      />

      <IssueRefundDrawer
        open={refundDrawerOpen}
        onClose={() => setRefundDrawerOpen(false)}
        transaction={activeTransaction}
        onRefundCreated={() => {
          fetchPaymentTransactions({
            start: dateRange.start,
            end: dateRange.end,
            page: pagination.page,
            size: pagination.size,
            chargeItemCode: chargeCodeFilter || undefined,
          });
        }}
      />

      <div style={{ marginTop: 12 }}>
        <TotalsStrip totals={totals} />
      </div>
    </div>
  );
}
